<figure class="ol" [class.is-intro]="intro" [attr.aria-label]="summary">
  <figcaption class="ol__head">
    <!-- What went out: the day's totals, or the hour under the pointer -->
    <div class="ol__side ol__side--past">
      <p class="ol__label">Last {{ hours }} hours</p>
      @if (activeBar) {
        <p class="ol__fig">
          <span class="ol__range">{{ barRange(activeBar) }}</span>
          <strong>{{ formatCount(activeBar.total) }}</strong> sent
          <span class="ol__dim">{{ formatCount(activeBar.delivered) }} delivered</span>
          @if (activeBar.failed) {
            <span class="ol__bad">{{ formatCount(activeBar.failed) }} failed</span>
          }
        </p>
      } @else {
        @switch (state) {
          @case ("ready") {
            <p class="ol__fig">
              <strong>{{ formatCount(pastTotal) }}</strong> sent
              <span class="ol__dim">{{ deliveredShare }} delivered</span>
              @if (pastFailed) {
                <span class="ol__bad">{{ formatCount(pastFailed) }} failed</span>
              }
            </p>
          }
          @case ("loading") {
            <span class="bk-skel ol__skel" aria-hidden="true"></span>
          }
          @case ("unavailable") {
            <p class="ol__note">Hourly counts show here once the backend update is deployed.</p>
          }
          @case ("failed") {
            <p class="ol__note">
              The counts didn’t load.
              <button type="button" class="ol__retry" (click)="load()">Try again</button>
            </p>
          }
        }
      }
    </div>

    <!-- The line is drawn around this moment -->
    <div class="ol__now">
      <span class="ol__label">Now</span>
      <time class="ol__clock" [attr.datetime]="now.toISOString()">{{ clock }}</time>
    </div>

    <!-- What goes out next -->
    <div class="ol__side ol__side--next">
      <p class="ol__label">Next {{ hours }} hours</p>
      @if (queueLoading) {
        <span class="bk-skel ol__skel" aria-hidden="true"></span>
      } @else if (nextUp) {
        <p class="ol__fig">
          <strong>{{ formatCount(upcoming.length) }}</strong> scheduled
          @if (later) {
            <span class="ol__dim">{{ formatCount(later) }} after that</span>
          }
        </p>
      } @else {
        <p class="ol__fig ol__dim">Nothing scheduled</p>
      }
    </div>
  </figcaption>

  <div class="ol__plot">
    <!-- Hours still to come: hatched, waiting to be filled -->
    <span class="ol__future" aria-hidden="true"></span>

    <div class="ol__bars" [class.has-active]="activeBar" aria-hidden="true">
      @for (bar of bars; track bar.start.getTime(); let i = $index) {
        <span
          class="ol__bar"
          [class.is-active]="activeBar?.start?.getTime() === bar.start.getTime()"
          [class.has-sends]="bar.total > 0"
          [style.left.%]="bar.left"
          [style.width.%]="bar.width"
          (pointerenter)="showBar(bar)"
          (pointerleave)="showBar(null)"
        >
          <span class="ol__fill" [style.height.%]="barHeight(bar)" [style.--i]="i">
            <span class="ol__seg ol__seg--failed" [style.height.%]="share(bar.failed, bar)"></span>
            <span class="ol__seg ol__seg--pending" [style.height.%]="share(bar.pending, bar)"></span>
            <span class="ol__seg ol__seg--delivered" [style.height.%]="share(bar.delivered, bar)"></span>
          </span>
        </span>
      }
    </div>

    <span class="ol__axis" aria-hidden="true"></span>
    <span class="ol__nowline" aria-hidden="true"></span>
    <span class="ol__nowdot" aria-hidden="true"></span>

    <!-- An empty half points at the nearest send outside the window -->
    @if (state === "ready" && !pastTotal && !sent.length) {
      <div class="ol__empty ol__empty--past">
        @if (before) {
          <button type="button" class="ol__edge" (click)="open.emit(before)">
            <span class="bk-sym" aria-hidden="true">west</span>
            Last sent {{ whenOf(before) }}
          </button>
        } @else {
          <p>Nothing sent in the last {{ hours }} hours</p>
        }
      </div>
    }
    @if (!queueLoading && !upcoming.length) {
      <div class="ol__empty ol__empty--next">
        @if (beyond) {
          <button type="button" class="ol__edge" (click)="open.emit(beyond)">
            Next send {{ whenOf(beyond) }}
            <span class="bk-sym" aria-hidden="true">east</span>
          </button>
        } @else {
          <p>Nothing scheduled in the next {{ hours }} hours</p>
        }
      </div>
    }

    <!-- Sends that went out -->
    @for (mark of sent; track mark.message.uuid) {
      <button
        type="button"
        class="ol__pin"
        [style.left.%]="mark.left"
        [attr.aria-label]="mark.description"
        (click)="open.emit(mark.message)"
      >
        <span class="ol__tip" [class.is-start]="mark.left < 14" aria-hidden="true">
          <strong>{{ subjectOf(mark.message) }}</strong>
          Sent {{ mark.clock }}
        </span>
      </button>
    }

    <!-- Schedules that passed without going out -->
    @for (mark of missed; track mark.message.uuid) {
      <button
        type="button"
        class="ol__pin ol__pin--missed"
        [style.left.%]="mark.left"
        [attr.aria-label]="mark.description"
        (click)="open.emit(mark.message)"
      >
        <span class="ol__tip" [class.is-start]="mark.left < 14" aria-hidden="true">
          <strong>{{ subjectOf(mark.message) }}</strong>
          Due {{ mark.clock }} · hasn’t gone out
        </span>
      </button>
    }

    <!-- Scheduled sends; the next one carries its countdown -->
    @for (mark of upcoming; track mark.message.uuid; let first = $first) {
      <button
        type="button"
        class="ol__mark"
        [class.is-next]="first"
        [class.is-flip]="mark.left > (first ? 76 : 88)"
        [style.left.%]="mark.left"
        [attr.aria-label]="mark.description"
        (click)="open.emit(mark.message)"
      >
        <span class="ol__stem" aria-hidden="true"></span>
        <span class="ol__ring" aria-hidden="true"></span>
        @if (first) {
          <span class="ol__lead" aria-hidden="true">
            <strong>{{ countdown(mark.at) }}</strong>
            <span>{{ subjectOf(mark.message) }}</span>
          </span>
        } @else {
          @if (mark.clock) {
            <span class="ol__time" aria-hidden="true">{{ mark.clock }}</span>
          }
          <span class="ol__tip ol__tip--mark" [class.is-end]="mark.left > 86" aria-hidden="true">
            <strong>{{ subjectOf(mark.message) }}</strong>
            {{ whenOf(mark.message) }} · {{ countdown(mark.at) }}
          </span>
        }
      </button>
    }
  </div>

  <div class="ol__ticks" aria-hidden="true">
    @for (tick of ticks; track $index) {
      <span
        class="ol__tick"
        [class.is-day]="tick.day"
        [class.is-off]="tick.left < 3 || tick.left > 97"
        [style.left.%]="tick.left"
      >
        <span class="ol__tick-clock">{{ tick.clock }}</span>
        @if (tick.day) {
          <span class="ol__tick-day">{{ tick.day }}</span>
        }
      </span>
    }
  </div>
</figure>
